/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
// eslint-disable-next-line no-restricted-imports
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import {
    changeStatusDialogDataVar,
    setEditMode,
    statusChangedVar,
} from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInListVar,
    TemplateScope,
    shareTemplateRequestDialogDataVar,
} from '../../../apollo/reactiveVariables/TemplateManagementVariables';
import { Paths } from '../../../common/enums/Paths';
import {
    EntryPoint,
    PanelRegion,
    PanelType,
    ScenarioName,
    StatusName,
    StepName,
    SubWorkLoad,
    LogCategory,
} from '../../../logger';
import { getThemedIcon, IconName } from '../../../resources/icons/Icons';
import { generateDeepLink } from '../../../service/DeepLinkManager/DeepLinkManager';
import { DeepLinkType } from '../../../service/DeepLinkManager/IDeepLinkManager';
import {
    ApprovalTemplateInstance,
    ListApprovalTemplateInstancesScenario,
    ListOrgApprovalTemplateInstancesClient,
    ListTeamApprovalTemplateInstancesClient,
} from '../../../service/gen/templates';

import { copyContentToClip } from '../../../utilities/CopyContent2Clipboard';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ShareTemplateLinkModal } from '../ShareTemplateLink/ShareTemplateLinkModal.component';

import {
    TemplateManagementListComponent,
    TemplateManagementListComponentProps,
} from './TemplateManagementList.component';

export const TemplateManagementListContainer: React.FC = (): JSX.Element => {
    const logger = getAppHost().getLogger();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [templateInstances, setTemplateInstances] = React.useState<ApprovalTemplateInstance[]>([]);
    const [showError, setShowError] = React.useState(false);
    const shareTemplateRequestDialogData = useReactiveVar(shareTemplateRequestDialogDataVar);

    const history = useHistory();
    const currentScope = useReactiveVar(currentScopeInListVar);
    const changeStatusData = useReactiveVar(statusChangedVar);
    const loc = getAppHost().getLocale();
    const createRequestText = loc.strings.share_template_link_createrequest;

    const getTemplateInstanceList = (): void => {
        if (currentScope.scope === TemplateScope.None) {
            return;
        }

        setLoading(true);
        const startTime = performance.now();
        let requestPromise: Promise<ApprovalTemplateInstance[]>;

        if (currentScope.scope === TemplateScope.Team) {
            requestPromise = new ListTeamApprovalTemplateInstancesClient().listTeamApprovalTemplateInstances(
                currentScope.team?.id,
                ListApprovalTemplateInstancesScenario.Management
            );
        } else {
            requestPromise = new ListOrgApprovalTemplateInstancesClient()
                .listOrgApprovalTemplateInstances(ListApprovalTemplateInstancesScenario.Management)
                .then((result: ApprovalTemplateInstance[]): ApprovalTemplateInstance[] => {
                    let templateInstanceList;
                    if (currentScope.scope === TemplateScope.TenantSpecificPeople) {
                        templateInstanceList = result.filter((value: ApprovalTemplateInstance): boolean => {
                            return !value.IsVisibleToAll;
                        });
                    } else {
                        templateInstanceList = result.filter((value: ApprovalTemplateInstance): boolean => {
                            return value.IsVisibleToAll;
                        });
                    }

                    return templateInstanceList;
                });
        }

        requestPromise
            .then((result: ApprovalTemplateInstance[]): void => {
                logger.markScenario(
                    ScenarioName.TemplateManagement,
                    StepName.LoadTemplateInstanceList,
                    StatusName.Success,
                    {
                        stepElapsed: performance.now() - startTime,
                        data: {
                            Scope: currentScope.scope,
                            TeamId: currentScope.team?.id,
                        },
                    }
                );

                setTemplateInstances(result);
                setShowError(false);
            })
            .catch((error: any): any => {
                logger.logTraceHttpException('ListApprovalTemplateInstance', error.message, error.status);
                logger.markScenario(
                    ScenarioName.TemplateManagement,
                    StepName.LoadTemplateInstanceList,
                    StatusName.Fail,
                    {
                        stepElapsed: performance.now() - startTime,
                        data: {
                            Scope: currentScope.scope,
                            TeamId: currentScope.team?.id,
                        },
                    }
                );
                setShowError(true);
            })
            .finally((): void => {
                setLoading(false);
            });
    };

    React.useEffect((): void => {
        getTemplateInstanceList();
    }, [currentScope]);

    const onStatusChange = (templateInstanceId: string, targetStatus: boolean, templateInstanceTitle: string): void => {
        logger.markScenario(ScenarioName.TemplateManagement, StepName.OpenChangeStatusModule, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.TemplateManagement,
            StepName.OpenChangeStatusModule,
            'click',
            'nav',
            'templateInstanceListItem',
            'templateInstanceList',
            PanelRegion.Main,
            PanelType.TemplateManagement
        );

        changeStatusDialogDataVar({ isOpen: true, targetStatus, templateInstanceId, templateInstanceTitle });
    };

    React.useEffect((): void => {
        if (changeStatusData) {
            const newList = [...templateInstances];
            newList.forEach((item: ApprovalTemplateInstance): void => {
                if (changeStatusData.templateInstanceId === item.Id) {
                    item.IsEnabled = changeStatusData.status;
                }
            });

            setTemplateInstances(newList);
        }
    }, [changeStatusData]);

    const onItemInvoked = (item: ApprovalTemplateInstance): void => {
        logger.updateCommonProperty({ templateInstanceId: item.Id });
        logger.createScenario(ScenarioName.EditTemplateInstance, EntryPoint.PersonalApp);
        logger.logUserBiView('templateInstanceListItem', PanelRegion.Main, PanelType.TemplateManagement);

        logger.markScenario(ScenarioName.TemplateManagement, StepName.OpenTemplateInstanceWizard, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.TemplateManagement,
            StepName.OpenTemplateInstanceWizard,
            'click',
            'nav',
            'templateInstanceListItem',
            'templateInstanceList',
            PanelRegion.Main,
            PanelType.TemplateManagement
        );

        setEditMode(item.Id, currentScope.scope, currentScope.team);
        history.push({
            pathname: Paths.BasePath + Paths.CreateTemplateInstance,
        });
    };

    const onCopyAction = (): void => {
        logger.logTraceInfo(LogCategory.ShareLink, 'Copy action');
        copyContentToClip(createRequestText, shareTemplateRequestDialogData.templateDefinitionTitle, linkUrl);
    };

    const onConfirmAction = (): void => {
        logger.logTraceInfo(LogCategory.ShareLink, 'Confirm ShareLink.');
        shareTemplateRequestDialogDataVar({
            isOpen: false,
            templateDefinitionId: null,
            templateDefinitionTitle: null,
            templateAllowAnyoneSubmission: null,
        });
    };

    const linkDescriptionText = `${loc.strings.share_template_link_link_to} '${shareTemplateRequestDialogData.templateDefinitionTitle}' ${loc.strings.share_template_link_copied}`;

    const linkPermissionSettingText = shareTemplateRequestDialogData.templateAccessText;
    const link = {
        activityType: DeepLinkType.ShareLink,
        objectId: shareTemplateRequestDialogData.templateDefinitionId,
    };
    const linkUrl = generateDeepLink(link, false);

    const props: TemplateManagementListComponentProps = {
        items: !loading ? templateInstances : [],
        loading,
        showError,
        onRetry: getTemplateInstanceList,
        showVisibilityScope: currentScope.scope === TemplateScope.TenantSpecificPeople,
        onStatusChange,
        onItemInvoked,
    };

    return (
        <div>
            {shareTemplateRequestDialogData.isOpen && (
                <ShareTemplateLinkModal
                    shareTemplateLinkCloseButton={'close'}
                    shareTemplateLinkCopyButton={loc.strings.share_template_link_copy}
                    onDismiss={onConfirmAction}
                    copyContentToClip={onCopyAction}
                    isOpen={true}
                    copyLinkSuccessInfo={linkDescriptionText}
                    linkPermissionSettingText={linkPermissionSettingText}
                    generatedLink={linkUrl}
                    CopySuccessIcon={getThemedIcon(IconName.checkMark)}
                    LinkPermissionSettingIcon={getThemedIcon(IconName.SecurityGroup)}
                />
            )}
            <TemplateManagementListComponent {...props} />
        </div>
    );
};
